/**
 * FE-side region → ISO 4217 currency code map. Mirrors the currency used
 * by `Zaveato.Domain.Common.RegionDefaults.CurrencyFor(regionCode)` on the
 * backend.
 *
 * SRP: THE single source of truth for FE code that needs the *default*
 * currency for a region — new-shop seeding preview, empty-state
 * currency picker, admin "create shop" modal, preferences dropdowns.
 *
 * NOT for entity display: existing orders / appointments / invoices /
 * products carry their own currency snapshot (BE-authoritative). Feed
 * those into `formatMoney(amount, entity.currency)` — never look them
 * up from region.
 *
 * OCP: adding a new region = one line here. Kept structurally identical
 * to `regionDialCodes.ts` / `regionTimezones.ts` / `addressFormats.ts`
 * so a "new region" PR is a small predictable diff across four files.
 *
 * Unknown / null / empty region falls back to `GBP` — matches BE
 * `RegionDefaults.FallbackRegion = "GB"`.
 */
import type { RegionCode } from '@/config/hosts'

const REGION_CURRENCIES: Record<RegionCode, string> = {
  GB: 'GBP',
  PK: 'PKR',
  AE: 'AED',
  SA: 'SAR',
}

/** Fallback ISO 4217 code when the region code is unknown / null / missing. */
export const FALLBACK_CURRENCY_CODE = 'GBP'

/**
 * Resolve a region code to its ISO 4217 currency code. Case-insensitive,
 * whitespace-tolerant. Unknown → `FALLBACK_CURRENCY_CODE`.
 */
export function getCurrencyForRegion(regionCode: string | null | undefined): string {
  const normalised = (regionCode ?? '').trim().toUpperCase() as RegionCode
  return REGION_CURRENCIES[normalised] ?? FALLBACK_CURRENCY_CODE
}

/**
 * All region-default currencies, in a stable region order. Feed into
 * preference / admin dropdowns that let the user pick a currency without
 * being told *which shop* it belongs to.
 */
export const SUPPORTED_REGION_CURRENCIES: ReadonlyArray<{
  regionCode: RegionCode
  currencyCode: string
}> = (Object.entries(REGION_CURRENCIES) as Array<[RegionCode, string]>).map(
  ([regionCode, currencyCode]) => ({ regionCode, currencyCode }),
)
